/**
 * One message of the conversation: the analyst's words with what came with
 * them, or the Assistant's reply as it happened — its text, the tools it
 * ran, the work it started, the asks it raised and what it proposed — or a
 * run's closing statement, recorded in the conversation as the case agent
 * wrote it.
 */

import { useState } from 'react';

import { Badge, Button, Group, Loader, Stack, Text } from '@mantine/core';

import type { CopilotProposal, CopilotMessage as Message } from '@/types/copilot';
import { CopilotText } from './CopilotText';
import type { CaseLink } from './markdown';
import { ProposalCard } from './ProposalCard';
import { AskChip, TaskChip, ToolChip } from './TaskChip';
import classes from './Copilot.module.css';

const ENDED: Record<string, { label: string; color: string }> = {
  failed: { label: 'Failed', color: 'red' },
  stopped: { label: 'Stopped', color: 'gray' },
  interrupted: { label: 'Interrupted', color: 'orange' },
};

function clip(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

interface CopilotMessageProps {
  message: Message;
  proposals: Map<string, CopilotProposal>;
  people: Record<string, string>;
  /** The reply's state while it streams (`thinking` · `writing`). */
  state?: string;
  onOpenLink: (link: CaseLink) => void;
  onApply: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  /** Several of a reply's edits applied together, as one new draft. */
  onApplyMany?: (ids: string[]) => Promise<void>;
}

export function CopilotMessage({
  message,
  proposals,
  people,
  state,
  onOpenLink,
  onApply,
  onDismiss,
  onApplyMany,
}: Readonly<CopilotMessageProps>) {
  // The reply's open edits, chosen for applying together: every one until
  // the person unticks some (null: all).
  const [chosen, setChosen] = useState<ReadonlySet<string> | null>(null);
  const [applying, setApplying] = useState(false);
  const openEdits = message.blocks.flatMap((block) => {
    if (block.type !== 'proposal') {
      return [];
    }
    const proposal = proposals.get(block.proposalId);
    return proposal && proposal.kind === 'edit' && proposal.status === 'proposed'
      ? [proposal.id]
      : [];
  });
  const together = Boolean(onApplyMany) && openEdits.length > 1;
  const picked = openEdits.filter((id) => chosen === null || chosen.has(id));
  const choose = (id: string, on: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev ?? openEdits);
      if (on) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  const applyChosen = async () => {
    if (!onApplyMany || picked.length === 0) {
      return;
    }
    setApplying(true);
    try {
      await onApplyMany(picked);
      setChosen(null);
    } finally {
      setApplying(false);
    }
  };
  if (message.role === 'analyst') {
    const passage = message.selection?.passage;
    const focus = message.selection?.focus;
    return (
      <Stack gap={4} className={classes.analyst} data-testid="copilot-asked">
        {message.author ? (
          <Text size="xs" c="dimmed">
            {people[message.author] ?? message.author}
          </Text>
        ) : null}
        {passage ? (
          <Text size="xs" className={classes.quote}>
            “{clip(passage.quote, 240)}”
          </Text>
        ) : null}
        {focus ? (
          <Text size="xs" c="dimmed">
            About {focus.type.replace(/_/g, ' ')}
            {focus.label ? `: ${focus.label}` : ''}
          </Text>
        ) : null}
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {message.text}
        </Text>
      </Stack>
    );
  }
  const running = message.status === 'running';
  const ended = ENDED[message.status];
  const lastText = message.blocks.map((b) => b.type).lastIndexOf('text');
  return (
    <Stack gap={6} className={classes.reply} data-testid="copilot-reply">
      {message.kind === 'run' ? (
        <Badge
          size="xs"
          variant="light"
          color="blue"
          style={{ alignSelf: 'flex-start', textTransform: 'none' }}
          data-testid="copilot-run-statement"
        >
          The case agent&apos;s statement
        </Badge>
      ) : null}
      {message.blocks.map((block, index) => {
        switch (block.type) {
          case 'text':
            return (
              <CopilotText
                key={index}
                text={block.text}
                onOpenLink={onOpenLink}
                writing={running && state === 'writing' && index === lastText}
              />
            );
          case 'tool':
            return <ToolChip key={index} block={block} />;
          case 'task':
            return <TaskChip key={index} block={block} onOpenLink={onOpenLink} />;
          case 'ask':
            return <AskChip key={index} block={block} onOpenLink={onOpenLink} />;
          case 'proposal':
            return (
              <ProposalCard
                key={index}
                proposal={proposals.get(block.proposalId) ?? null}
                summary={block.summary}
                kind={block.kind}
                people={people}
                onApply={onApply}
                onDismiss={onDismiss}
                chosen={together ? picked.includes(block.proposalId) : undefined}
                onChoose={together && openEdits.includes(block.proposalId) ? choose : undefined}
              />
            );
          default:
            return null;
        }
      })}
      {together ? (
        <Group gap="xs" justify="flex-end" data-testid="copilot-apply-together">
          <Text size="xs" c="dimmed">
            {picked.length} of {openEdits.length} edits chosen — applied as one new draft
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={() => setChosen(picked.length === openEdits.length ? new Set() : null)}
            data-testid="copilot-choose-all"
          >
            {picked.length === openEdits.length ? 'Choose none' : 'Choose all'}
          </Button>
          <Button
            size="compact-xs"
            disabled={picked.length === 0}
            loading={applying}
            onClick={() => void applyChosen()}
            data-testid="copilot-apply-chosen"
          >
            Apply {picked.length} chosen
          </Button>
        </Group>
      ) : null}
      {running ? (
        <Group gap={6}>
          <Loader size={12} />
          <Text size="xs" c="dimmed">
            {state === 'writing' ? 'Writing…' : 'Thinking…'}
          </Text>
        </Group>
      ) : null}
      {ended ? (
        <Group gap={6} wrap="nowrap" align="flex-start">
          <Badge size="xs" variant="light" color={ended.color}>
            {ended.label}
          </Badge>
          {message.error ? (
            <Text size="xs" c="dimmed">
              {message.error}
            </Text>
          ) : null}
        </Group>
      ) : null}
      {message.status === 'done' && Number(message.costUsd) > 0 ? (
        <Text size="xs" c="dimmed" data-testid="copilot-cost">
          ${Number(message.costUsd).toFixed(2)} · {message.modelCalls} model call
          {message.modelCalls === 1 ? '' : 's'}
        </Text>
      ) : null}
    </Stack>
  );
}
